import { EventManager } from './EventManager';
import { Logger } from './Logger';

export enum LoadingPhase {
    NotStarted = 'not-started',
    SceneSetup = 'scene-setup',
    CameraSetup = 'camera-setup',
    POILoading = 'poi-loading',
    MaterialLoading = 'material-loading',
    LayoutApplication = 'layout-application',
    Ready = 'ready'
}

export interface LoadingProgress {
    phase: LoadingPhase;
    progress: number; // 0-100
    message: string;
    details?: string;
}

export interface LoadingTask {
    id: string;
    name: string;
    weight: number; // Relative weight for progress calculation
    isCompleted: boolean;
    progress: number; // 0-100 for this specific task
}

/**
 * LoadingManager - Handles progressive loading with visual feedback
 * Ensures smooth initialization and prevents render stuttering
 */
export class LoadingManager {
    private eventManager: EventManager;
    private currentPhase: LoadingPhase = LoadingPhase.NotStarted;
    private tasks: Map<string, LoadingTask> = new Map();
    private phaseWeights: Map<LoadingPhase, number> = new Map();
    private loadingOverlay: HTMLElement | null = null;
    private targetCanvas: HTMLCanvasElement | null = null;
    private progressBar: HTMLElement | null = null;
    private statusText: HTMLElement | null = null;
    private detailsText: HTMLElement | null = null;
    private isComplete: boolean = false;
    // #1760: timestamps so the watchdog reports what actually happened instead of "stuck".
    private completedAt = 0;
    private hideStartedAt = 0;
    
    // Promise resolvers for waiting
    private readyPromise: Promise<void>;
    private readyResolve: ((value: void) => void) | null = null;
    
    constructor(eventManager: EventManager) {
        this.eventManager = eventManager;
        this.setupPhaseWeights();
        this.createLoadingUI();
        
        // Create promise for waiting until ready
        this.readyPromise = new Promise<void>((resolve) => {
            this.readyResolve = resolve;
        });
    }
    
    private setupPhaseWeights(): void {
        // Define how much each phase contributes to overall progress
        this.phaseWeights.set(LoadingPhase.SceneSetup, 10);
        this.phaseWeights.set(LoadingPhase.CameraSetup, 15);
        this.phaseWeights.set(LoadingPhase.POILoading, 25);
        this.phaseWeights.set(LoadingPhase.MaterialLoading, 35);
        this.phaseWeights.set(LoadingPhase.LayoutApplication, 10);
        this.phaseWeights.set(LoadingPhase.Ready, 5);
    }
    
    private createLoadingUI(): void {
        // Inject keyframes once
        const styleEl = document.createElement('style');
        styleEl.textContent = `
            @keyframes presenter-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            @keyframes presenter-pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
            @keyframes presenter-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
            #loading-overlay.presenter-fade-out { opacity: 0; transition: opacity 0.4s ease; pointer-events: none; }
        `;
        document.head.appendChild(styleEl);

        // Full-screen overlay
        this.loadingOverlay = document.createElement('div');
        this.loadingOverlay.id = 'loading-overlay';
        this.loadingOverlay.style.cssText = `
            position: fixed; inset: 0; z-index: 10000;
            background: var(--presenter-bg, radial-gradient(120% 100% at 30% 20%, #D4141F 0%, #C1121C 45%, #8E0C14 100%));
            display: flex; flex-direction: column;
            justify-content: center; align-items: center;
            font-family: -apple-system, 'SF Pro Display', 'Inter', system-ui, sans-serif;
            color: rgba(255,255,255,0.95);
            animation: presenter-fade-in 0.4s ease;
        `;

        // Spinner: minimal rotating ring
        const spinner = document.createElement('div');
        spinner.style.cssText = `
            width: 48px; height: 48px;
            border: 2px solid rgba(251,247,244,0.22);
            border-top-color: rgba(251,247,244,0.95);
            border-radius: 50%;
            animation: presenter-rotate 0.9s linear infinite;
            margin-bottom: 32px;
        `;

        // Status text — primary
        this.statusText = document.createElement('div');
        this.statusText.textContent = 'Wird geladen';
        this.statusText.style.cssText = `
            font-family: 'Iowan Old Style', Palatino, Georgia, serif;
            font-size: 21px; font-weight: 400;
            letter-spacing: 0.2px;
            margin-bottom: 14px;
            color: #FBF7F4;
        `;

        // Thin progress bar
        // Kein sichtbarer Fortschrittsbalken mehr (Alex-Review 2026-08-03: die
        // horizontale Linie zerschneidet die Flaeche). Der Balken bleibt im DOM, damit
        // die Fortschritts-Logik unveraendert weiterlaeuft — nur unsichtbar.
        const progressContainer = document.createElement('div');
        progressContainer.style.cssText = `
            width: 0; height: 0; overflow: hidden; opacity: 0;
        `;
        this.progressBar = document.createElement('div');
        this.progressBar.style.cssText = `
            width: 0%; height: 100%;
            background: rgba(255,255,255,0.85);
            border-radius: 1px;
            transition: width 0.3s ease;
        `;
        progressContainer.appendChild(this.progressBar);

        // Details text — small subtle
        this.detailsText = document.createElement('div');
        this.detailsText.style.cssText = `
            font-size: 12px; font-weight: 400;
            color: rgba(251,247,244,0.6);
            margin-top: 4px;
            min-height: 1.2em;
            letter-spacing: 0.2px;
            animation: presenter-pulse 2s ease infinite;
        `;

        this.loadingOverlay.appendChild(spinner);
        this.loadingOverlay.appendChild(this.statusText);
        this.loadingOverlay.appendChild(progressContainer);
        this.loadingOverlay.appendChild(this.detailsText);

        document.body.appendChild(this.loadingOverlay);
    }

    /** Allow callers to set overlay gradient dynamically (from SceneSettingsData) */
    public setBackgroundGradient(angleDeg: number, color1: string, color2: string): void {
        if (!this.loadingOverlay) return;
        const css = `linear-gradient(${angleDeg}deg, ${color1} 0%, ${color2} 100%)`;
        (this.loadingOverlay.style as any).setProperty('--presenter-bg', css);
    }

    /** Attach canvas so we can blur/disable input while overlay is active */
    public attachCanvas(canvas: HTMLCanvasElement | null): void {
        this.targetCanvas = canvas;
        if (this.targetCanvas) {
            this.targetCanvas.style.transition = 'filter 0.3s ease';
            this.targetCanvas.style.filter = 'blur(6px)';
            this.targetCanvas.style.pointerEvents = 'none';
        }
    }
    
    /**
     * Start a new loading phase
     */
    public startPhase(phase: LoadingPhase, message: string): void {
        this.currentPhase = phase;
        this.tasks.clear(); // Clear tasks from previous phase
        
        //Logger.Instance.Info(`🔄 Loading Phase: ${phase} - ${message}`, 'Loading');
        
        this.updateUI({
            phase,
            progress: this.calculateOverallProgress(),
            message,
            details: undefined
        });
        
        this.eventManager.emit('loading:phase-started', { phase, message });
    }
    
    /**
     * Add a task to the current phase
     */
    public addTask(id: string, name: string, weight: number = 1): void {
        const task: LoadingTask = {
            id,
            name,
            weight,
            isCompleted: false,
            progress: 0
        };
        
        this.tasks.set(id, task);
        //Logger.Instance.Debug(`📋 Added loading task: ${name} (weight: ${weight})`, 'Loading');
    }
    
    /**
     * Update task progress
     */
    public updateTask(id: string, progress: number, details?: string): void {
        const task = this.tasks.get(id);
        if (!task) {
            Logger.Instance.Warn(`⚠️ Unknown loading task: ${id}`, 'Loading');
            return;
        }
        
        task.progress = Math.max(0, Math.min(100, progress));
        task.isCompleted = task.progress >= 100;
        
        const overallProgress = this.calculateOverallProgress();
        const statusMessage = task.name;
        
        this.updateUI({
            phase: this.currentPhase,
            progress: overallProgress,
            message: statusMessage,
            details
        });
        
        //Logger.Instance.Debug(`📊 Task progress: ${task.name} = ${progress}%`, 'Loading');
        
        this.eventManager.emit('loading:task-updated', { 
            taskId: id, 
            progress, 
            overallProgress 
        });
    }
    
    /**
     * Complete a task
     */
    public completeTask(id: string): void {
        this.updateTask(id, 100);
    }
    
    /**
     * Complete current phase
     */
    public completePhase(): void {
        // Mark all tasks as completed
        this.tasks.forEach(task => {
            task.isCompleted = true;
            task.progress = 100;
        });
        
        const overallProgress = this.calculateOverallProgress();
        //Logger.Instance.Info(`✅ Completed Phase: ${this.currentPhase} (${overallProgress}%)`, 'Loading');
        
        this.eventManager.emit('loading:phase-completed', { 
            phase: this.currentPhase, 
            progress: overallProgress 
        });
    }
    
    /**
     * Mark loading as complete
     */
    public complete(): void {
        this.isComplete = true;
        this.currentPhase = LoadingPhase.Ready;
        this.completedAt = LoadingManager.now();
        
        this.updateUI({
            phase: LoadingPhase.Ready,
            progress: 100,
            message: 'Ready',
            details: ''
        });
        
        // Fade out loading screen after a short delay
        setTimeout(() => {
            this.hideLoadingScreen();
        }, 1000);

        // Watchdog: falls das Overlay trotz complete() haengen bleibt (beobachtet
        // 2026-08-16, vermutlich Seitenaufruf mitten im Deploy-Fenster mit halb
        // ausgetauschten Assets), nach 6s hart entfernen — ein ewiger 'Ready 100%'-
        // Spinner ist in jedem Fall schlechter als eine sichtbare Buehne.
        //
        // #1760: Auf einem blockierten Main-Thread (SwiftShader, GLB-Parsing) feuern
        // die verspaeteten Timer beim Freiwerden in Faelligkeitsreihenfolge: erst der
        // 1-s-Hide (der seinen 500-ms-Entfernen-Timer NEU plant), direkt danach der
        // ueberfaellige 6-s-Watchdog — der das Overlay noch sieht, obwohl das Ausblenden
        // laeuft. Deshalb: laeuft das Ausblenden bereits, nicht eingreifen, nur messen.
        setTimeout(() => {
            if (!this.loadingOverlay || !this.loadingOverlay.parentNode) return;
            const elapsed = Math.round(LoadingManager.now() - this.completedAt);
            const lateBy = Math.max(0, elapsed - 6000);
            if (this.hideStartedAt) {
                Logger.Instance.Info(
                    `Loading overlay hide in progress ${elapsed}ms after complete() — watchdog ${lateBy}ms late (main thread stalled), letting the fade finish`,
                    'Loading'
                );
                return;
            }
            Logger.Instance.Warn(
                `Loading overlay stuck ${elapsed}ms after complete() (hide never started, watchdog ${lateBy}ms late) — force removing`,
                'Loading'
            );
            this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
            this.loadingOverlay = null;
            if (this.targetCanvas) {
                this.targetCanvas.style.filter = '';
                this.targetCanvas.style.pointerEvents = '';
            }
        }, 6000);
        
        //Logger.Instance.Info('🎉 Loading complete - Starting render loop', 'Loading');
        this.eventManager.emit('loading:complete');
        
        // Resolve the ready promise
        if (this.readyResolve) {
            this.readyResolve();
            this.readyResolve = null;
        }
    }
    
    /**
     * Wait until loading is complete
     */
    public async waitUntilReady(): Promise<void> {
        if (this.isComplete) {
            return Promise.resolve();
        }
        return this.readyPromise;
    }
    
    /**
     * Get current loading progress (0-100)
     */
    public getProgress(): number {
        return this.calculateOverallProgress();
    }
    
    /**
     * Check if loading is complete
     */
    public isReady(): boolean {
        return this.isComplete;
    }
    
    private calculateOverallProgress(): number {
        const phases = [
            LoadingPhase.SceneSetup,
            LoadingPhase.CameraSetup,
            LoadingPhase.POILoading,
            LoadingPhase.MaterialLoading,
            LoadingPhase.LayoutApplication,
            LoadingPhase.Ready
        ];
        
        let totalWeight = 0;
        let completedWeight = 0;
        
        for (const phase of phases) {
            const weight = this.phaseWeights.get(phase) || 0;
            totalWeight += weight;
            
            if (phase === this.currentPhase) {
                // Calculate progress within current phase
                const phaseProgress = this.calculatePhaseProgress();
                completedWeight += (weight * phaseProgress) / 100;
            } else if (phases.indexOf(phase) < phases.indexOf(this.currentPhase)) {
                // Phase is completed
                completedWeight += weight;
            }
            // Future phases contribute 0
        }
        
        return totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0;
    }
    
    private calculatePhaseProgress(): number {
        if (this.tasks.size === 0) return 0;
        
        let totalWeight = 0;
        let completedWeight = 0;
        
        this.tasks.forEach(task => {
            totalWeight += task.weight;
            completedWeight += (task.weight * task.progress) / 100;
        });
        
        return totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;
    }
    
    private updateUI(progress: LoadingProgress): void {
        if (!this.progressBar || !this.statusText || !this.detailsText) return;
        
        // Update progress bar
        this.progressBar.style.width = `${progress.progress}%`;
        
        // Update status text
        this.statusText.textContent = progress.message;
        
        // Update details text
        if (progress.details) {
            this.detailsText.textContent = progress.details;
        }
        
        // Add percentage to details if not specified
        if (!progress.details) {
            this.detailsText.textContent = `${progress.progress}%`;
        }
        
        this.eventManager.emit('loading:progress-updated', progress);
    }
    
    private hideLoadingScreen(): void {
        if (!this.loadingOverlay) return;
        this.hideStartedAt = LoadingManager.now();
        
        // Fade out animation
        this.loadingOverlay.style.transition = 'opacity 0.5s ease';
        this.loadingOverlay.style.opacity = '0';
        
        setTimeout(() => {
            if (this.loadingOverlay && this.loadingOverlay.parentNode) {
                this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
                this.loadingOverlay = null;
            }
            if (this.targetCanvas) {
                this.targetCanvas.style.filter = '';
                this.targetCanvas.style.pointerEvents = '';
            }
        }, 500);
    }
    
    /** Monotonic clock with a Node fallback (harness). */
    private static now(): number {
        return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
    }

    /** #1760 harness hook: has the fade-out started, and when did complete() run. */
    public getOverlayTimeline(): { completedAt: number; hideStartedAt: number; overlayAttached: boolean } {
        return { completedAt: this.completedAt, hideStartedAt: this.hideStartedAt, overlayAttached: Boolean(this.loadingOverlay && this.loadingOverlay.parentNode) };
    }

    /**
     * Clean up resources
     */
    public dispose(): void {
        this.hideLoadingScreen();
        this.tasks.clear();
        this.eventManager.removeAllListeners('loading:*');
    }
} 